/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useCallback} from 'react'
import PropTypes from 'prop-types'
import {
    Button,
    Box,
    Center,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link as RouteLink} from 'react-router-dom'
import useTagManager from '../../hooks/use-tag-manager'
import {scheduleIdleAnalytics} from '../../utils/common-utils'

/**
 * The Swatch Component displays item inside `SwatchGroup`. For proper keyboard accessibility,
 * ensure that the rendered elements can receive keyboard focus, and are immediate siblings.
 */
const Swatch = ({
    children,
    disabled,
    unavailable = disabled,
    href,
    label,
    name,
    product,
    selected,
    id,
    value,
    onClick,
    ispdp = false,
    isCart = false,
    isProductPartOfSet = false,
    swatchProps = {},
    variant = 'square',
    attributeId,
    isCustomSwatch,
    ariaLabel,
    customSwatchSelect = () => {}
}) => {
    const accessibleLabel = ariaLabel || `${name}${unavailable ? ', sold out' : ''}`
    const tagManager = useTagManager()
    const styles = useMultiStyleConfig('SwatchGroup', {
        variant,
        disabled,
        selected,
        ispdp: !isProductPartOfSet && ispdp
    })
    const onSelect = useCallback(
        (e) => {
            e.preventDefault()
            onClick(value)
        },
        [onClick, value]
    )

    // Derive the handler directly. Storing it in state caused every swatch to render again
    // after its parent rendered, which made PLP swatch interactions expensive on mobile.
    const shouldSelect = onClick && !(href && (isProductPartOfSet || ispdp))
    const toProps = isProductPartOfSet ? {to: href} : {}
    return (
        <Box
            padding={ispdp ? 0 : variant === 'square' ? 0 : '5px'}
            {...(swatchProps?.display ? {display: swatchProps?.display} : {})}
            className="swatch-box"
            data-disabled={unavailable ? 'true' : undefined}
        >
            <Button
                {...styles.swatch}
                {...swatchProps}
                {...toProps}
                as={href && !isProductPartOfSet && !isCustomSwatch ? RouteLink : 'button'}
                aria-label={accessibleLabel}
                className="product-swatch"
                aria-checked={selected}
                variant="outline"
                role="radio"
                onClick={(event) => {
                    if (isCart || isCustomSwatch) {
                        customSwatchSelect({[attributeId]: value})
                    }
                    if (!isCart && ispdp && id && id !== 'size' && id !== 'letter') {
                        const key =
                            id === 'color' ||
                            id === 'stoneColor' ||
                            id === 'original' ||
                            product?.c_PDPSwatches?.indexOf(name) !== -1
                                ? 'color'
                                : id

                        scheduleIdleAnalytics(() => {
                            const storageKey = 'monetateSwatch-' + key
                            const currentSelection = localStorage.getItem(storageKey)

                            if (currentSelection) {
                                localStorage.setItem(storageKey, currentSelection + ',' + name)
                            } else {
                                localStorage.setItem(storageKey, name)
                            }

                            tagManager.pdpColorSelected(
                                name,
                                id
                                    ?.replace(/-/g, ' ')
                                    ?.replace(/!/g, '')
                                    ?.replace(/\b\w/g, (c) => c.toUpperCase())
                            )
                        })
                    }
                    if (shouldSelect) {
                        onSelect(event)
                    }
                }}
                // To mimic the behavior of native radio inputs, only one input should be focusable.
                // (The rest are selectable via arrow keys.)
            >
                <Center {...styles.swatchButton}>
                    {children}
                    {label && <Box className="swatch-label">{label}</Box>}
                </Center>
            </Button>
        </Box>
    )
}

Swatch.displayName = 'Swatch'

Swatch.propTypes = {
    /**
     * The children to be rendered within swatch item.
     */
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    /**
     * Indicates whether the option is disabled
     */
    disabled: PropTypes.bool,
    /**
     * Exposes an unavailable state independently from visual disabled styling.
     */
    unavailable: PropTypes.bool,
    /**
     * Indicates whether the option is selected.
     * This props is provided internally by SwatchGroup
     */
    selected: PropTypes.bool,
    /**
     * The shape of the Swatch
     */
    variant: PropTypes.oneOf(['square', 'circle', 'charm']),
    /**
     * The label of the option.
     */
    label: PropTypes.string,
    /**
     *  The url of this option
     */
    href: PropTypes.string,
    /**
     * The display value for each swatch
     */
    name: PropTypes.string,
    /**
     * The value for the option.
     */
    value: PropTypes.string,
    /**
     * This function is called whenever the mouse enters the swatch on desktop or when clicked on mobile.
     * The values is passed as the first argument.
     */
    onClick: PropTypes.func,
    /**
     * Additional props to be passed to the swatch button
     */
    swatchProps: PropTypes.object,
    product: PropTypes.any,
    ispdp: PropTypes.bool,
    id: PropTypes.string,
    isCart: PropTypes.bool,
    isProductPartOfSet: PropTypes.bool,
    /**
     * The id of the attribute
     */
    attributeId: PropTypes.string,
    /**
     * Indicates whether the swatch is a custom swatch
     */
    isCustomSwatch: PropTypes.bool,
    /**
     * Accessible label including the option state.
     */
    ariaLabel: PropTypes.string,
    /**
     * Function to be called when a custom swatch is selected
     */
    customSwatchSelect: PropTypes.func
}

export default Swatch
